import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Users, Calendar, QrCode, DollarSign, BarChart3, Search, ChevronDown, ChevronUp, Shield, UserCircle, Package, Check, Clock, X as XIcon, Eye, UserPlus, CheckCircle, XCircle, Ticket, Trash2, Database, AlertTriangle, Gift } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatEventDateTime } from '../utils/dateFormat';
import ActivityFeedCard from '../components/activity/ActivityFeedCard';
import GiftCodeModal from '../components/admin/GiftCodeModal';
import BulkDeleteEventsModal from '../components/admin/BulkDeleteEventsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StatusBadge({ status }) {
  const styles = {
    pending_approval: 'bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/30',
    access_granted: 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30',
    used: 'bg-[#16A34A]/10 text-[#16A34A] border-[#16A34A]/30',
    revoked: 'bg-[#C44545]/10 text-[#C44545] border-[#C44545]/30',
    scanned: 'bg-[#16A34A]/10 text-[#16A34A] border-[#16A34A]/30'
  };
  const labels = {
    pending_approval: 'Pending',
    access_granted: 'Granted',
    used: 'Checked In',
    revoked: 'Revoked',
    scanned: 'Scanned'
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase border rounded ${styles[status] || 'bg-[#1A1A1A] text-[#AFAFAF] border-[#1A1A1A]'}`} data-testid={`status-badge-${status}`}>
      {labels[status] || status}
    </span>
  );
}

export default function AdminDashboard({ user }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [ticketSearch, setTicketSearch] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [hostApps, setHostApps] = useState([]);
  const [appProcessing, setAppProcessing] = useState(null);
  const [eventHostFilter, setEventHostFilter] = useState('');
  const [eventStatusFilter, setEventStatusFilter] = useState('');
  const [giftCodeOpen, setGiftCodeOpen] = useState(false);
  const [eventDateFrom, setEventDateFrom] = useState('');
  const [eventDateTo, setEventDateTo] = useState('');
  const [eventSearch, setEventSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [dataSearch, setDataSearch] = useState('');
  const [dataView, setDataView] = useState('users');
  // Bulk delete selection — Set of event_ids; opens modal when > 0 + Delete click
  const [selectedEventIds, setSelectedEventIds] = useState(() => new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, ticketsRes, eventsRes, usersRes, appsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/tickets`, { headers }),
        axios.get(`${API}/admin/events`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/host-applications`, { headers })
      ]);
      setStats(statsRes.data);
      setTickets(ticketsRes.data);
      setEvents(eventsRes.data);
      setUsers(usersRes.data);
      setHostApps(appsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleAppAction = async (applicationId, action) => {
    setAppProcessing(applicationId);
    try {
      const token = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/host-applications/${applicationId}/${action}`, {}, { headers });
      toast.success(`Application ${action === 'approve' ? 'approved' : 'rejected'}`);
      // Refresh applications and users (role may have changed)
      const [appsRes, usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/host-applications`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/stats`, { headers })
      ]);
      setHostApps(appsRes.data);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} application`);
    } finally {
      setAppProcessing(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm || deleteInput !== 'DELETE') return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}` };
      const endpoint = deleteConfirm.type === 'user'
        ? `${API}/admin/users/${deleteConfirm.id}`
        : `${API}/admin/events/${deleteConfirm.id}`;
      const res = await axios.delete(endpoint, { headers });
      toast.success(res.data.message);
      setDeleteConfirm(null);
      setDeleteInput('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const eventMap = {};
  events.forEach(e => { eventMap[e.event_id] = e; });

  const filteredTickets = tickets.filter(t => {
    if (ticketSearch) {
      const q = ticketSearch.toLowerCase();
      const matchName = t.guest_name?.toLowerCase().includes(q);
      const matchEmail = t.guest_email?.toLowerCase().includes(q);
      const matchNumber = t.ticket_number?.toLowerCase().includes(q);
      const matchEvent = eventMap[t.event_id]?.title?.toLowerCase().includes(q);
      if (!matchName && !matchEmail && !matchNumber && !matchEvent) return false;
    }
    if (eventFilter && t.event_id !== eventFilter) return false;
    if (statusFilter && t.status !== statusFilter) return false;
    return true;
  });

  const hostUsers = useMemo(() => users.filter(u => u.role === 'host' || u.role === 'admin'), [users]);

  const filteredEvents = useMemo(() => {
    let list = [...events];
    if (eventHostFilter) list = list.filter(e => e.host_id === eventHostFilter);
    if (eventStatusFilter) list = list.filter(e => (e.status || 'active') === eventStatusFilter);
    if (eventSearch) {
      const q = eventSearch.toLowerCase();
      list = list.filter(e => e.title?.toLowerCase().includes(q) || e.event_id?.toLowerCase().includes(q));
    }
    if (eventDateFrom) list = list.filter(e => e.date >= eventDateFrom);
    if (eventDateTo) list = list.filter(e => e.date <= eventDateTo);
    return list;
  }, [events, eventHostFilter, eventStatusFilter, eventSearch, eventDateFrom, eventDateTo]);

  const dataFilteredItems = useMemo(() => {
    const q = dataSearch.toLowerCase();
    if (dataView === 'users') {
      return q ? users.filter(u => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)) : users;
    }
    return q ? events.filter(e => e.title?.toLowerCase().includes(q) || e.event_id?.toLowerCase().includes(q)) : events;
  }, [dataView, dataSearch, users, events]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const pendingAppsCount = hostApps.filter(a => a.status === 'pending').length;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <div className="border-b border-[#1A1A1A] bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Shield className="w-7 h-7 text-[#D4AF37]" />
              <div>
                <h1 className="text-3xl font-bold text-[#EAE7E1] tracking-tight">Admin Dashboard</h1>
                <p className="text-sm text-[#AFAFAF]">Platform-level overview</p>
              </div>
              {pendingAppsCount > 0 && (
                <button
                  onClick={() => setActiveTab('host-apps')}
                  className="flex items-center gap-1.5 ml-2 px-3 py-1.5 bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-sm text-[#FFB800] text-xs font-semibold hover:bg-[#FFB800]/15 transition-colors"
                  data-testid="header-pending-apps-badge"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  {pendingAppsCount} pending
                </button>
              )}
            </div>
            <div className="flex items-center flex-wrap gap-2">
              <Button onClick={() => navigate('/my-tickets')} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" data-testid="admin-my-passes-button"><Ticket className="w-4 h-4 mr-2" />My Passes</Button>
              <Button onClick={() => navigate('/account')} variant="ghost" className="text-[#AFAFAF] hover:text-[#EAE7E1]" data-testid="admin-account-button"><UserCircle className="w-5 h-5" /></Button>
              <Button onClick={() => navigate('/admin/reports')} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" data-testid="admin-reports-button"><BarChart3 className="w-4 h-4 mr-2" />Reports</Button>
              <Button onClick={() => setGiftCodeOpen(true)} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" data-testid="admin-gift-code-button"><Gift className="w-4 h-4 mr-2" />Create Gift Code</Button>
              <Button onClick={() => navigate('/settings/platform')} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm hidden sm:inline-flex" data-testid="platform-settings-button">Platform Settings</Button>
              <Button onClick={() => navigate('/dashboard')} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" data-testid="host-dashboard-button">Host View</Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Recent Activity (admin global) */}
        <div className="mb-8">
          <ActivityFeedCard limit={10} title="Recent Activity (All Hosts)" defaultCollapsed={true} />
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 mb-8" data-testid="admin-stats-grid">
            {[
              ['Total Events', stats.total_events, Calendar, null],
              ['Total Users', stats.total_users, Users, null],
              ['Total Hosts', stats.total_hosts, Users, null],
              ['Total Passes', stats.total_tickets, QrCode, null],
              ['Total Revenue', `$${stats.total_revenue?.toFixed(2)}`, DollarSign, 'text-[#D4AF37]'],
              ['Attendees', stats.total_attendees, BarChart3, null],
            ].map(([label, value, Icon, color]) => (
              <div key={label} className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex items-center space-x-2 mb-2">
                  <Icon className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs font-medium text-[#777] uppercase tracking-wide">{label}</span>
                </div>
                <div className={`text-2xl font-bold ${color || 'text-[#EAE7E1]'}`}>{value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Status breakdown */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="status-breakdown">
            {[
              ['Pending', stats.pending_tickets, Clock, 'text-[#FFB800]'],
              ['Active', stats.active_tickets, Check, 'text-[#D4AF37]'],
              ['Checked In', stats.scanned_tickets, QrCode, 'text-[#16A34A]'],
              ['Revoked', stats.revoked_tickets, XIcon, 'text-[#C44545]'],
            ].map(([label, value, Icon, color]) => (
              <div key={label} className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-3 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon className={`w-4 h-4 ${color}`} />
                  <span className="text-sm text-[#AFAFAF]">{label}</span>
                </div>
                <span className={`text-lg font-bold ${color}`}>{value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-1 mb-6" data-testid="admin-tabs">
          {[['overview', 'All Orders'], ['events', 'Events'], ['users', 'Users'], ['host-apps', 'Host Applications'], ['data', 'Data']].map(([key, label]) => {
            const pendingCount = key === 'host-apps' ? hostApps.filter(a => a.status === 'pending').length : 0;
            return (
              <button key={key} onClick={() => setActiveTab(key)} className={`px-5 py-2 rounded-sm text-sm font-semibold transition-colors flex items-center gap-2 ${activeTab === key ? 'bg-[#D4AF37] text-[#050505]' : 'text-[#AFAFAF] hover:text-[#EAE7E1]'}`} data-testid={`admin-tab-${key}`}>
                {label}
                {pendingCount > 0 && (
                  <span className={`inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full ${activeTab === key ? 'bg-[#050505] text-[#D4AF37]' : 'bg-[#D4AF37] text-[#050505]'}`} data-testid="host-apps-pending-count">{pendingCount}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* All Orders Tab */}
        {activeTab === 'overview' && (
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm" data-testid="orders-table">
            <div className="p-4 border-b border-[#1A1A1A]">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" />
                  <Input value={ticketSearch} onChange={(e) => setTicketSearch(e.target.value)} placeholder="Search by name, email, ticket #, event..." className="pl-10" data-testid="ticket-search" />
                </div>
                <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} className="px-3 py-2 bg-[#050505] border border-[#1A1A1A] rounded-sm text-sm text-[#EAE7E1]" data-testid="event-filter">
                  <option value="">All Events</option>
                  {events.map(e => <option key={e.event_id} value={e.event_id}>{e.title}</option>)}
                </select>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 bg-[#050505] border border-[#1A1A1A] rounded-sm text-sm text-[#EAE7E1]" data-testid="status-filter">
                  <option value="">All Statuses</option>
                  <option value="pending_approval">Pending</option>
                  <option value="access_granted">Granted</option>
                  <option value="used">Checked In</option>
                  <option value="revoked">Revoked</option>
                </select>
              </div>
              <div className="text-xs text-[#555] mt-2">{filteredTickets.length} of {tickets.length} orders</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#050505] border-b border-[#1A1A1A]">
                  <tr>
                    {['Guest', 'Event', 'Entry', 'Add-Ons', 'Amount', 'Method', 'Status', ''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[#777] uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1A1A1A]">
                  {filteredTickets.length === 0 ? (
                    <tr><td colSpan="8" className="px-4 py-12 text-center text-[#AFAFAF]">No orders found</td></tr>
                  ) : (
                    filteredTickets.slice(0, 100).map(ticket => {
                      const evt = eventMap[ticket.event_id];
                      const isExpanded = expandedTicket === ticket.ticket_id;
                      const hasAddOns = ticket.purchased_add_ons?.length > 0;
                      return (
                        <React.Fragment key={ticket.ticket_id}>
                          <tr className="hover:bg-[#050505]/50 cursor-pointer" onClick={() => setExpandedTicket(isExpanded ? null : ticket.ticket_id)} data-testid={`order-row-${ticket.ticket_id}`}>
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-[#EAE7E1]">{ticket.guest_name}</div>
                              <div className="text-xs text-[#777]">{ticket.guest_email}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-[#EAE7E1] max-w-[200px] truncate">{evt?.title || ticket.event_id}</div>
                              <div className="text-xs text-[#555]">{evt ? formatEventDateTime(evt.date, evt.time) : ''}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-[#EAE7E1]">{ticket.pass_tier_name || 'General'}</div>
                              <div className="text-xs text-[#777]">x{ticket.entry_quantity || 1} ({ticket.attendees_count || 1} pax)</div>
                            </td>
                            <td className="px-4 py-3">
                              {hasAddOns ? (
                                <div className="flex items-center space-x-1">
                                  <Package className="w-3.5 h-3.5 text-[#D4AF37]" />
                                  <span className="text-sm text-[#D4AF37]">{ticket.purchased_add_ons.length}</span>
                                </div>
                              ) : (
                                <span className="text-xs text-[#555]">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-[#D4AF37]">${(ticket.amount_paid || 0).toFixed(2)}</td>
                            <td className="px-4 py-3 text-xs text-[#AFAFAF]">{ticket.payment_method_used || '-'}</td>
                            <td className="px-4 py-3"><StatusBadge status={ticket.status} /></td>
                            <td className="px-4 py-3 text-[#555]">{isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</td>
                          </tr>
                          {isExpanded && (
                            <tr><td colSpan="8" className="px-4 py-4 bg-[#050505]">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-testid={`order-detail-${ticket.ticket_id}`}>
                                <div><span className="text-xs text-[#777] block">Ticket #</span><span className="text-[#EAE7E1] font-mono">{ticket.ticket_number}</span></div>
                                <div><span className="text-xs text-[#777] block">Confirmation</span><span className="text-[#EAE7E1]">{ticket.payment_confirmation || '-'}</span></div>
                                <div><span className="text-xs text-[#777] block">Issued</span><span className="text-[#EAE7E1]">{ticket.issued_at ? new Date(ticket.issued_at).toLocaleString() : '-'}</span></div>
                                <div><span className="text-xs text-[#777] block">Scanned</span><span className="text-[#EAE7E1]">{ticket.scanned_at ? new Date(ticket.scanned_at).toLocaleString() : 'Not yet'}</span></div>
                                {hasAddOns && (
                                  <div className="col-span-2 md:col-span-4">
                                    <span className="text-xs text-[#D4AF37] block mb-1">Add-Ons Purchased</span>
                                    <div className="flex flex-wrap gap-2">
                                      {ticket.purchased_add_ons.map((ao) => (
                                        <span key={ao.add_on_id || ao.name} className="px-2 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs rounded border border-[#D4AF37]/20">
                                          {ao.name} x{ao.quantity} (${ao.price})
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {ticket.is_comp && (
                                  <div className="col-span-2"><span className="text-xs text-[#777] block">Comp Note</span><span className="text-[#F2D27A]">{ticket.comp_note || 'Complimentary'}</span></div>
                                )}
                                <div className="col-span-2 md:col-span-4 flex space-x-2">
                                  <Button onClick={(e) => { e.stopPropagation(); navigate(`/ticket/${ticket.ticket_id}`); }} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm text-xs" data-testid={`view-ticket-${ticket.ticket_id}`}>
                                    <Eye className="w-3 h-3 mr-1" /> View Pass
                                  </Button>
                                </div>
                              </div>
                            </td></tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm" data-testid="events-table">
            {/* Filter bar */}
            <div className="p-4 border-b border-[#1A1A1A]">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" />
                  <Input value={eventSearch} onChange={e => setEventSearch(e.target.value)} placeholder="Search events..." className="pl-10" data-testid="event-search" />
                </div>
                <select value={eventHostFilter} onChange={e => setEventHostFilter(e.target.value)} className="px-3 py-2 bg-[#050505] border border-[#1A1A1A] rounded-sm text-sm text-[#EAE7E1]" data-testid="event-host-filter">
                  <option value="">All Hosts</option>
                  {hostUsers.map(h => <option key={h.user_id} value={h.user_id}>{h.name || h.email}</option>)}
                </select>
                <select value={eventStatusFilter} onChange={e => setEventStatusFilter(e.target.value)} className="px-3 py-2 bg-[#050505] border border-[#1A1A1A] rounded-sm text-sm text-[#EAE7E1]" data-testid="event-status-filter">
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                  <option value="deleted">Deleted</option>
                </select>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-3">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-[#777] whitespace-nowrap">From</label>
                  <input type="date" value={eventDateFrom} onChange={e => setEventDateFrom(e.target.value)} className="px-3 py-1.5 bg-[#050505] border border-[#1A1A1A] rounded-sm text-sm text-[#EAE7E1]" data-testid="event-date-from" />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-[#777] whitespace-nowrap">To</label>
                  <input type="date" value={eventDateTo} onChange={e => setEventDateTo(e.target.value)} className="px-3 py-1.5 bg-[#050505] border border-[#1A1A1A] rounded-sm text-sm text-[#EAE7E1]" data-testid="event-date-to" />
                </div>
                {(eventHostFilter || eventStatusFilter || eventDateFrom || eventDateTo || eventSearch) && (
                  <button onClick={() => { setEventHostFilter(''); setEventStatusFilter(''); setEventDateFrom(''); setEventDateTo(''); setEventSearch(''); }} className="text-xs text-[#C44545] hover:text-[#F77] transition-colors" data-testid="clear-event-filters">Clear filters</button>
                )}
              </div>
              <div className="text-xs text-[#555] mt-2">{filteredEvents.length} of {events.length} events</div>
              {selectedEventIds.size > 0 && (
                <div className="flex items-center justify-between gap-3 mt-3 bg-[#C44545]/5 border border-[#C44545]/30 rounded-sm px-3 py-2" data-testid="bulk-delete-bar">
                  <div className="text-xs text-[#EAE7E1]">
                    <span className="font-semibold text-[#C44545]">{selectedEventIds.size}</span> event{selectedEventIds.size === 1 ? '' : 's'} selected
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedEventIds(new Set())}
                      className="text-xs text-[#777] hover:text-[#EAE7E1]"
                      data-testid="clear-bulk-selection"
                    >Clear</button>
                    <Button
                      onClick={() => setShowBulkDelete(true)}
                      className="bg-[#C44545] hover:bg-[#E25555] text-white text-xs rounded-sm py-1.5 h-auto"
                      data-testid="open-bulk-delete-btn"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                      Bulk Delete Events
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#050505] border-b border-[#1A1A1A]">
                  <tr>
                    <th className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        aria-label="Select all visible events"
                        checked={filteredEvents.length > 0 && filteredEvents.every(e => selectedEventIds.has(e.event_id))}
                        onChange={(e) => {
                          const next = new Set(selectedEventIds);
                          if (e.target.checked) filteredEvents.forEach(ev => next.add(ev.event_id));
                          else filteredEvents.forEach(ev => next.delete(ev.event_id));
                          setSelectedEventIds(next);
                        }}
                        data-testid="bulk-select-all"
                      />
                    </th>
                    {['Event', 'Host', 'Date', 'Capacity', 'Entry Tiers', 'Add-Ons', 'Status'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[#777] uppercase tracking-wide">{h}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1A1A1A]">
                  {filteredEvents.length === 0 ? (
                    <tr><td colSpan="8" className="px-4 py-12 text-center text-[#AFAFAF]">No events match filters</td></tr>
                  ) : (
                    filteredEvents.map(evt => {
                      const host = users.find(u => u.user_id === evt.host_id);
                      const entryTiers = (evt.pass_tiers || []).filter(t => (t.type || 'entry') === 'entry');
                      const addonTiers = (evt.pass_tiers || []).filter(t => t.type === 'addon');
                      const isSelected = selectedEventIds.has(evt.event_id);
                      return (
                        <tr key={evt.event_id} className={`hover:bg-[#050505]/50 ${isSelected ? 'bg-[#C44545]/5' : ''}`} data-testid={`event-row-${evt.event_id}`}>
                          <td className="px-3 py-3">
                            <input
                              type="checkbox"
                              aria-label={`Select ${evt.title}`}
                              checked={isSelected}
                              onChange={() => {
                                const next = new Set(selectedEventIds);
                                if (next.has(evt.event_id)) next.delete(evt.event_id);
                                else next.add(evt.event_id);
                                setSelectedEventIds(next);
                              }}
                              data-testid={`bulk-select-${evt.event_id}`}
                            />
                          </td>
                          <td className="px-4 py-3"><div className="text-sm font-medium text-[#EAE7E1]">{evt.title}</div><div className="text-xs text-[#555]">{evt.event_id}</div></td>
                          <td className="px-4 py-3">
                            <button onClick={() => { setEventHostFilter(evt.host_id); }} className="text-sm text-[#AFAFAF] hover:text-[#D4AF37] transition-colors text-left" data-testid={`filter-by-host-${evt.event_id}`}>
                              {host?.name || host?.email || evt.host_id}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm text-[#AFAFAF]">{formatEventDateTime(evt.date, evt.time)}</td>
                          <td className="px-4 py-3 text-sm text-[#EAE7E1]">{evt.capacity}</td>
                          <td className="px-4 py-3 text-sm text-[#AFAFAF]">{entryTiers.length > 0 ? entryTiers.map(t => t.tier_name).join(', ') : '-'}</td>
                          <td className="px-4 py-3 text-sm text-[#D4AF37]">{addonTiers.length > 0 ? addonTiers.length : '-'}</td>
                          <td className="px-4 py-3"><span className={`text-xs font-bold uppercase ${evt.status === 'archived' ? 'text-[#555]' : 'text-[#D4AF37]'}`}>{evt.status || 'active'}</span></td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm" data-testid="users-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#050505] border-b border-[#1A1A1A]">
                  <tr>{['User', 'Email', 'Role', 'Saved Tiers', 'Created'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[#777] uppercase tracking-wide">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-[#1A1A1A]">
                  {users.map(u => (
                    <tr key={u.user_id} className="hover:bg-[#050505]/50" data-testid={`user-row-${u.user_id}`}>
                      <td className="px-4 py-3 text-sm font-medium text-[#EAE7E1]">{u.name || 'Unnamed'}</td>
                      <td className="px-4 py-3 text-sm text-[#AFAFAF]">{u.email}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${u.role === 'admin' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : u.role === 'host' ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30' : u.role === 'staff' ? 'bg-[#16A34A]/10 text-[#16A34A]' : 'bg-[#1A1A1A] text-[#AFAFAF]'}`}>{u.role}</span></td>
                      <td className="px-4 py-3 text-sm text-[#AFAFAF]">{u.saved_pass_tiers?.length || 0}</td>
                      <td className="px-4 py-3 text-xs text-[#555]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Host Applications Tab */}
        {activeTab === 'host-apps' && (
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm" data-testid="host-applications-table">
            <div className="p-4 border-b border-[#1A1A1A] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="text-sm font-semibold text-[#EAE7E1] uppercase tracking-wide">Host Applications</h2>
              </div>
              <span className="text-xs text-[#777]">{hostApps.length} total &middot; {hostApps.filter(a => a.status === 'pending').length} pending</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#050505] border-b border-[#1A1A1A]">
                  <tr>{['Applicant', 'Email', 'Reason', 'Submitted', 'Status', 'Actions'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[#777] uppercase tracking-wide">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-[#1A1A1A]">
                  {hostApps.length === 0 ? (
                    <tr><td colSpan="6" className="px-4 py-12 text-center text-[#AFAFAF]">No host applications yet</td></tr>
                  ) : (
                    hostApps.map(app => (
                      <tr key={app.application_id} className="hover:bg-[#050505]/50" data-testid={`host-app-row-${app.application_id}`}>
                        <td className="px-4 py-3 text-sm font-medium text-[#EAE7E1]">{app.name || 'Unknown'}</td>
                        <td className="px-4 py-3 text-sm text-[#AFAFAF]">{app.email}</td>
                        <td className="px-4 py-3 text-sm text-[#AFAFAF] max-w-[300px]">
                          <span className="whitespace-pre-wrap break-words">{app.reason || <span className="text-[#555] italic">No reason given</span>}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#777]">{app.created_at ? new Date(app.created_at).toLocaleDateString() : '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase border rounded ${
                            app.status === 'pending' ? 'bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/30' :
                            app.status === 'approved' ? 'bg-[#16A34A]/10 text-[#16A34A] border-[#16A34A]/30' :
                            'bg-[#C44545]/10 text-[#C44545] border-[#C44545]/30'
                          }`} data-testid={`host-app-status-${app.application_id}`}>{app.status}</span>
                        </td>
                        <td className="px-4 py-3">
                          {app.status === 'pending' ? (
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleAppAction(app.application_id, 'approve')}
                                disabled={appProcessing === app.application_id}
                                className="bg-[#16A34A] hover:bg-[#15803D] text-white text-xs px-3 py-1 h-7 rounded-sm"
                                data-testid={`approve-btn-${app.application_id}`}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" /> Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleAppAction(app.application_id, 'reject')}
                                disabled={appProcessing === app.application_id}
                                className="border-[#C44545]/40 text-[#C44545] hover:bg-[#C44545]/10 text-xs px-3 py-1 h-7 rounded-sm"
                                data-testid={`reject-btn-${app.application_id}`}
                              >
                                <XCircle className="w-3 h-3 mr-1" /> Reject
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-[#555]">{app.reviewed_at ? `Reviewed ${new Date(app.reviewed_at).toLocaleDateString()}` : 'Processed'}</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Data Management Tab */}
        {activeTab === 'data' && (
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm" data-testid="data-management-section">
            <div className="p-4 border-b border-[#1A1A1A]">
              <div className="flex items-center gap-2 mb-3">
                <Database className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="text-sm font-semibold text-[#EAE7E1] uppercase tracking-wide">Data Management</h2>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex gap-1">
                  {[['users', 'Users'], ['events', 'Events']].map(([key, label]) => (
                    <button key={key} onClick={() => { setDataView(key); setDataSearch(''); }} className={`px-4 py-1.5 rounded-sm text-xs font-semibold transition-colors ${dataView === key ? 'bg-[#D4AF37] text-[#050505]' : 'text-[#AFAFAF] border border-[#1A1A1A] hover:text-[#EAE7E1]'}`} data-testid={`data-view-${key}`}>{label}</button>
                  ))}
                </div>
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" />
                  <Input value={dataSearch} onChange={e => setDataSearch(e.target.value)} placeholder={`Search ${dataView}...`} className="pl-10" data-testid="data-search" />
                </div>
              </div>
              <div className="text-xs text-[#555] mt-2">{dataFilteredItems.length} {dataView}</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#050505] border-b border-[#1A1A1A]">
                  {dataView === 'users' ? (
                    <tr>{['Name', 'Email', 'Role', 'Created', ''].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[#777] uppercase tracking-wide">{h}</th>)}</tr>
                  ) : (
                    <tr>{['Event', 'Host', 'Date', 'Status', ''].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[#777] uppercase tracking-wide">{h}</th>)}</tr>
                  )}
                </thead>
                <tbody className="divide-y divide-[#1A1A1A]">
                  {dataFilteredItems.length === 0 ? (
                    <tr><td colSpan="5" className="px-4 py-12 text-center text-[#AFAFAF]">No results</td></tr>
                  ) : dataView === 'users' ? (
                    dataFilteredItems.map(u => (
                      <tr key={u.user_id} className="hover:bg-[#050505]/50" data-testid={`data-user-${u.user_id}`}>
                        <td className="px-4 py-3 text-sm text-[#EAE7E1]">{u.name || 'Unnamed'}</td>
                        <td className="px-4 py-3 text-sm text-[#AFAFAF]">{u.email}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${u.role === 'admin' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : u.role === 'host' ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30' : 'bg-[#1A1A1A] text-[#AFAFAF]'}`}>{u.role}</span></td>
                        <td className="px-4 py-3 text-xs text-[#555]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                        <td className="px-4 py-3">
                          {u.role !== 'admin' && u.user_id !== user.user_id && (
                            <Button size="sm" variant="outline" onClick={() => setDeleteConfirm({ type: 'user', id: u.user_id, label: u.email })} className="border-[#C44545]/30 text-[#C44545] hover:bg-[#C44545]/10 text-xs h-7 rounded-sm" data-testid={`delete-user-${u.user_id}`}>
                              <Trash2 className="w-3 h-3 mr-1" /> Delete
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    dataFilteredItems.map(evt => {
                      const host = users.find(u => u.user_id === evt.host_id);
                      return (
                        <tr key={evt.event_id} className="hover:bg-[#050505]/50" data-testid={`data-event-${evt.event_id}`}>
                          <td className="px-4 py-3"><div className="text-sm text-[#EAE7E1]">{evt.title}</div><div className="text-xs text-[#555]">{evt.event_id}</div></td>
                          <td className="px-4 py-3 text-sm text-[#AFAFAF]">{host?.name || host?.email || evt.host_id}</td>
                          <td className="px-4 py-3 text-sm text-[#AFAFAF]">{formatEventDateTime(evt.date, evt.time)}</td>
                          <td className="px-4 py-3"><span className={`text-xs font-bold uppercase ${evt.status === 'archived' ? 'text-[#555]' : 'text-[#D4AF37]'}`}>{evt.status || 'active'}</span></td>
                          <td className="px-4 py-3">
                            <Button size="sm" variant="outline" onClick={() => setDeleteConfirm({ type: 'event', id: evt.event_id, label: evt.title })} className="border-[#C44545]/30 text-[#C44545] hover:bg-[#C44545]/10 text-xs h-7 rounded-sm" data-testid={`delete-event-${evt.event_id}`}>
                              <Trash2 className="w-3 h-3 mr-1" /> Delete
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="delete-confirmation-modal">
          <div className="bg-[#0A0A0A] border border-[#C44545]/30 rounded-sm p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-[#C44545]" />
              <h3 className="text-lg font-bold text-[#EAE7E1]">Confirm Deletion</h3>
            </div>
            <p className="text-sm text-[#AFAFAF] mb-1">You are about to permanently delete this {deleteConfirm.type}:</p>
            <p className="text-sm text-[#EAE7E1] font-mono bg-[#050505] px-3 py-2 rounded border border-[#1A1A1A] mb-4 break-all" data-testid="delete-target-label">{deleteConfirm.label}</p>
            <p className="text-xs text-[#777] mb-3">Type <strong className="text-[#C44545]">DELETE</strong> to confirm:</p>
            <Input value={deleteInput} onChange={e => setDeleteInput(e.target.value)} placeholder="Type DELETE" className="mb-4 bg-[#050505] border-[#1A1A1A] text-[#EAE7E1]" data-testid="delete-confirm-input" />
            <div className="flex gap-3">
              <Button onClick={() => { setDeleteConfirm(null); setDeleteInput(''); }} variant="outline" className="flex-1 border-[#1A1A1A] text-[#AFAFAF] rounded-sm" data-testid="delete-cancel-btn">Cancel</Button>
              <Button onClick={handleDelete} disabled={deleteInput !== 'DELETE' || deleting} className="flex-1 bg-[#C44545] hover:bg-[#A33] text-white rounded-sm font-bold disabled:opacity-40" data-testid="delete-confirm-btn">
                {deleting ? 'Deleting...' : 'Delete Permanently'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <GiftCodeModal open={giftCodeOpen} onClose={() => setGiftCodeOpen(false)} />
      {showBulkDelete && (
        <BulkDeleteEventsModal
          selectedEvents={events.filter(e => selectedEventIds.has(e.event_id))}
          onClose={() => setShowBulkDelete(false)}
          onDone={() => {
            setShowBulkDelete(false);
            setSelectedEventIds(new Set());
            fetchData();
          }}
        />
      )}
    </div>
  );
}
